import moment from 'moment';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import {
  ePlanCode,
  ePlanFrequency,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { ePlanCurrency } from '../../../../SubscriptionManager.enums';
import { useCancelSubscriptionContext } from '../CancelSubscription.context';
import NotSeeingResultsConfirmationModal from './NotSeeingResultsConfirmationModal/NotSeeingResultsConfirmationModal.component';
import PlanNotSuitableConfirmationModal from './PlanNotSuitableConfirmationModal/PlanNotSuitableConfirmationModal.component';
import PriceTooHighConfirmationModal from './PriceTooHighConfirmationModal/PriceTooHighConfirmationModal.component';
import TechnicalProblemsConfirmationModal from './TechnicalProblemsConfirmationModal/TechnicalProblemsConfirmationModal.component';
import PauseConfirmationModal from './PauseConfirmationModal.component';

export type tConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * The success screen after an accepted offer, chosen by the offer the server
 * confirmed it applied. Nothing is confirmed without that offer.
 */
export default function ConfirmationModal(props: tConfirmationModalProps) {
  const { isOpen, onClose } = props;
  const {
    cancellationReason,
    appliedRetentionOffer,
    appliedPlanPauseMonths,
    planNotSuitableInvoicePreview,
    retentionSessionCta,
    handleScheduleSession,
    handleScheduleConsultation,
    calculateDates,
  } = useCancelSubscriptionContext();

  const dates = calculateDates();

  const { plans } = useSubscriptionContext();

  if (!appliedRetentionOffer) return null;

  switch (appliedRetentionOffer.type) {
    case 'DOWNGRADE': {
      const basicPlan = plans?.[ePlanFrequency.MONTHLY]?.find(
        (plan) => plan.planCode === ePlanCode.PLAN_BASIC,
      );
      const basicPlanPrice = basicPlan ? basicPlan.totalPrice : null;
      const basicPlanCurrency =
        basicPlan?.currency === ePlanCurrency.MXN ? 'MXN' : 'USD';

      const previewData = planNotSuitableInvoicePreview?.data?.data;

      // RET-1651: prefer the deferred next-invoice net (next_invoice_amount) when the change
      // defers, then a real immediate total, and finally the Basic plan list price. total_amount
      // is 0 for a deferred downgrade, so a bare truthiness check on it alone would wrongly fall
      // back to the list price and hide the actual next-invoice number.
      const invoiceAmount =
        previewData?.next_invoice_amount ??
        (previewData?.total_amount ? previewData.total_amount : basicPlanPrice);

      // RET-1651: use the real next charge date (end of the current period), never a hardcoded
      // "14 days from now" — the preview carries no date, so fabricating one misled customers.
      const invoiceDate = dates.nextChargeDate;

      return (
        <PlanNotSuitableConfirmationModal
          isOpen={isOpen}
          basicPlanPrice={basicPlanPrice}
          basicPlanCurrency={basicPlanCurrency}
          nextInvoiceDate={invoiceDate}
          invoiceAmount={invoiceAmount}
          formatDate={dates.formatDate}
          onClose={onClose}
        />
      );
    }

    case 'EXTENSION':
      return cancellationReason === 'not_seeing_results' ? (
        <NotSeeingResultsConfirmationModal
          isOpen={isOpen}
          nextInvoiceDate={dates.nextChargeDate}
          formatDate={dates.formatDate}
          onScheduleConsultation={handleScheduleConsultation || onClose}
          sessionCta={retentionSessionCta}
          durationDays={appliedRetentionOffer.durationDays}
          onClose={onClose}
        />
      ) : (
        <TechnicalProblemsConfirmationModal
          isOpen={isOpen}
          nextInvoiceDate={dates.nextChargeDate}
          formatDate={dates.formatDate}
          onScheduleSession={handleScheduleSession || onClose}
          sessionCta={retentionSessionCta}
          durationDays={appliedRetentionOffer.durationDays}
          onClose={onClose}
        />
      );

    case 'DISCOUNT':
      return (
        <PriceTooHighConfirmationModal
          isOpen={isOpen}
          offer={appliedRetentionOffer}
          nextChargeDate={dates.nextChargeDate}
          // The discount runs for the offer's months from the next charge.
          returnToNormalDate={moment(dates.nextChargeDate).add(
            appliedRetentionOffer.durationMonths,
            'months',
          )}
          formatDate={dates.formatDate}
          onClose={onClose}
        />
      );
    case 'PLAN_PAUSE':
      return appliedPlanPauseMonths ? (
        <PauseConfirmationModal
          isOpen={isOpen}
          offer={appliedRetentionOffer}
          selectedMonths={appliedPlanPauseMonths}
          onClose={onClose}
        />
      ) : null;
  }
}
